import { useEffect, useRef, useState } from 'react'
import { Check, Copy, Eye, EyeOff, Loader2, ShieldCheck } from 'lucide-react'
import { api } from '../../lib/api'
import { meld, meldFout } from '../../lib/staat'

/* ===========================================================================
 *  Een code laten zien of kopiëren
 *
 *  De code komt pas van de server als iemand erom vraagt, en elke keer staat
 *  dat in het logboek (wie, wat, wanneer). In de lijst staat hij dus nooit:
 *  daar staan alleen puntjes.
 *
 *  Na dertig seconden gaat hij weer dicht. Een scherm met de alarmcode
 *  erop dat open blijft staan op de pc bij de kassa, is precies wat dit niet
 *  moet zijn.
 *
 *  Kopiëren zet de code op het klembord en haalt hem daar na een halve
 *  minuut weer af -- als het klembord dan nog steeds de code bevat. Dat
 *  laatste kan een browser niet altijd nakijken; dan blijft hij staan, en
 *  dat zegt de melding.
 * =========================================================================== */

const OPEN_SECONDEN = 30

export function Code({ id, veld = 'waarde', heeft, klein }: {
  id: string
  veld?: 'waarde' | 'geheim'
  heeft: boolean
  klein?: boolean
}) {
  const [waarde, setWaarde] = useState<string | null>(null)
  const [bezig, setBezig] = useState<'tonen' | 'kopieren' | null>(null)
  const [gekopieerd, setGekopieerd] = useState(false)
  const klok = useRef<number | undefined>(undefined)

  useEffect(() => () => window.clearTimeout(klok.current), [])

  if (!heeft) return <span className="code leeg stil">Geen {veld === 'geheim' ? 'notitie' : 'code'}</span>

  async function tonen() {
    if (waarde !== null) { setWaarde(null); return }
    setBezig('tonen')
    try {
      const w = await api.geheimTonen(id, veld, 'tonen')
      setWaarde(w)
      window.clearTimeout(klok.current)
      klok.current = window.setTimeout(() => setWaarde(null), OPEN_SECONDEN * 1000)
    } catch (e) {
      meldFout(e)
    } finally {
      setBezig(null)
    }
  }

  async function kopieren() {
    setBezig('kopieren')
    try {
      const w = waarde ?? await api.geheimTonen(id, veld, 'kopieren')
      await navigator.clipboard.writeText(w)
      setGekopieerd(true)
      setTimeout(() => setGekopieerd(false), 1600)
      meld(`Gekopieerd. Over ${OPEN_SECONDEN} seconden halen we hem van het klembord.`, 'info')
      window.setTimeout(async () => {
        try {
          const nu = await navigator.clipboard.readText()
          if (nu === w) await navigator.clipboard.writeText('')
        } catch { /* lezen mag niet altijd; dan blijft hij staan */ }
      }, OPEN_SECONDEN * 1000)
    } catch (e) {
      meldFout(e)
    } finally {
      setBezig(null)
    }
  }

  const lang = veld === 'geheim' || (waarde !== null && (waarde.includes('\n') || waarde.length > 60))

  return (
    <span className={`code ${klein ? 'klein' : ''} ${lang ? 'lang' : ''}`}>
      {waarde !== null
        ? (lang ? <pre className="code-waarde">{waarde}</pre> : <span className="code-waarde mono">{waarde || <em className="stil">leeg</em>}</span>)
        : <span className="code-punten" aria-label="verborgen">••••••••</span>}
      <span className="code-knoppen">
        <button type="button" className="knop k-stil klein alleen-icoon" onClick={() => void tonen()}
          title={waarde !== null ? 'Verbergen' : 'Laten zien (komt in het logboek)'} aria-label="Laten zien">
          {bezig === 'tonen' ? <Loader2 size={14} className="draai" /> : waarde !== null ? <EyeOff size={14} /> : <Eye size={14} />}
        </button>
        <button type="button" className="knop k-stil klein alleen-icoon" onClick={() => void kopieren()}
          title="Kopiëren (komt in het logboek)" aria-label="Kopiëren">
          {bezig === 'kopieren' ? <Loader2 size={14} className="draai" /> : gekopieerd ? <Check size={14} className="kleur-ok" /> : <Copy size={14} />}
        </button>
      </span>
    </span>
  )
}

/* ------------------------------------------------------------------ *
 *  De 2FA-code van nu
 *
 *  Blijft twee minuten lopen en vraagt elke periode een nieuwe; daarna
 *  gaat hij dicht. De server zet één regel per tien minuten in het logboek.
 * ------------------------------------------------------------------ */

export function TweeStap({ id }: { id: string }) {
  const [open, setOpen] = useState(false)
  const [code, setCode] = useState<{ code: string; over: number; periode: number } | null>(null)
  const [tel, setTel] = useState(0)
  const [gekopieerd, setGekopieerd] = useState(false)
  const begon = useRef(0)

  useEffect(() => {
    if (!open) return
    let weg = false
    let volgende: number | undefined
    begon.current = Date.now()
    async function haal() {
      try {
        const c = await api.geheimTotp(id)
        if (weg) return
        setCode(c)
        setTel(c.over)
        if (Date.now() - begon.current < 120000) volgende = window.setTimeout(haal, c.over * 1000 + 300)
        else setOpen(false)
      } catch (e) {
        meldFout(e)
        setOpen(false)
      }
    }
    void haal()
    return () => { weg = true; window.clearTimeout(volgende) }
  }, [open, id])

  useEffect(() => {
    if (!open) return
    const t = window.setInterval(() => setTel((x) => Math.max(0, x - 1)), 1000)
    return () => window.clearInterval(t)
  }, [open])

  if (!open) {
    return (
      <button type="button" className="knop k-gewoon klein" onClick={() => setOpen(true)} title="De code van nu (komt in het logboek)">
        <ShieldCheck size={14} /> 2FA-code
      </button>
    )
  }

  return (
    <span className="totp">
      {code ? (
        <>
          <span className="totp-code mono">{code.code.slice(0, 3)} {code.code.slice(3)}</span>
          <span className="totp-klok" style={{ ['--deel' as string]: String(tel / code.periode) }} title={`Nog ${tel} seconden`}>{tel}</span>
          <button type="button" className="knop k-stil klein alleen-icoon" aria-label="Kopiëren"
            onClick={() => { void navigator.clipboard.writeText(code.code).then(() => { setGekopieerd(true); setTimeout(() => setGekopieerd(false), 1500) }) }}>
            {gekopieerd ? <Check size={14} className="kleur-ok" /> : <Copy size={14} />}
          </button>
        </>
      ) : <Loader2 size={14} className="draai" />}
      <button type="button" className="knop k-stil klein" onClick={() => setOpen(false)}>Sluiten</button>
    </span>
  )
}

/* ------------------------------------------------------------------ *
 *  Een nieuw wachtwoord of een nieuwe code bedenken
 * ------------------------------------------------------------------ */

export function bedenk(soort: 'wachtwoord' | 'cijfers', lengte?: number): string {
  if (soort === 'cijfers') {
    const n = lengte ?? 6
    const r = crypto.getRandomValues(new Uint32Array(n))
    return [...r].map((x) => String(x % 10)).join('')
  }
  /* Zonder tekens die op elkaar lijken (l, 1, I, O, 0): een wachtwoord dat
     iemand van een briefje overtikt, moet in één keer goed gaan. */
  const abc = 'abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789!#%&*+-=?@'
  const n = lengte ?? 20
  const r = crypto.getRandomValues(new Uint32Array(n))
  return [...r].map((x) => abc[x % abc.length]).join('')
}
